import { httpRequest, parseQuery } from '@calendly/http-tools'
import { Logger } from '@calendly/logging-service'
import { Segment } from '@calendly/segment'
import detect from 'detect.js'

import { setBookingRequestQueryParam, setFormSnapshot, cleanupAsyncBooking } from 'services/async_booking'
import { i18n } from 'locales'
import { eventService } from 'services/analytics/event_service'
import { selectedSpotsSelector } from 'selectors/calendar'
import { calendarSpotsCountSelector } from 'selectors/calendars'
import { maybeStoreData } from 'services/autofill'
import GoogleAnalytics from 'services/analytics/google'
import FbPixel from 'services/analytics/facebook_pixel'
import EmbedAnalytics from 'services/analytics/embed'
import {
  filledFieldsSelector,
  analyticsDataSelector,
  embedSelector,
  schedulingEventSelector,
  inviteeDataSelector,
  formSubmissionUuidSelector,
  showRecaptchaChallengeSelector,
  customFieldsSelector,
  isBookingAfterStripeRedirectSelector,
  savedFormDataForStripeRedirectSelector
} from 'selectors/scheduling'
import { paymentTokenSelector, saveFormDataForStripeRedirectSelector, stripeClientSecretSelector } from 'selectors/payment'
import {
  setGeneralError,
  setScheduleError,
} from 'actions/errors'
import { navigateToConfirmationPage } from 'actions/confirmation'
import { validateScheduling } from 'actions/validation'
import { createPayment } from 'actions/payment'
import { reschedule } from 'actions/rescheduling'
import { setSelectedSpots } from 'actions/spot'
import {
  schedulingLinkSelector
} from 'selectors/event_type'
import { eventTypeSelector, purchasedPackageSelector } from 'root-api'
import { countryCodeSelector } from 'selectors'
import { trackingSelector } from 'selectors/tracking'
import { inviteeSelector } from 'selectors/invitee'
import { currentUserSelector } from 'selectors/user'
import { Settings } from 'services/settings'
import { isDialCodeOnly} from 'services/phone_number'

const handleErrorResponse = (responseData) => (
  responseData.payload && responseData.payload.failure
    ? setGeneralError(responseData)
    : setScheduleError(responseData)
)

const handleSuccessResponse = (responseData) => ({
  type: 'SCHEDULING_DONE',
  payload: responseData
})

const afterScheduleNavigation = (formData) => (dispatch) => {
  const query = parseQuery(window.location.search)

  if (query.embed_type === 'intercom_chat') {
    dispatch({ type: 'INTERCOM_SCHEDULING_DONE' })
  } else {
    dispatch(navigateToConfirmationPage(formData))
  }
}

const scheduleDone = (success, responseData) => (
  success && (
    !responseData.additional_payment_auth_required
  )
)

const cardActionRequired = (success, responseData) => (
  success && responseData.additional_payment_auth_required
)

const duplicateBookingDetected = (success, responseData) => (
  !success && responseData.duplicate_in_progress
)

export const saveStripeRedirectSession = (paymentIntentId, formData) => async (dispatch) => {
  if (!paymentIntentId || !formData) {
    dispatch(setScheduleError({
      payload: {
        failure: {
          error: i18n.t('booking.error.default')
        }
      }
    }))
    return false
  }

  const [success, responseData] = await httpRequest({
    url: '/api/booking/stripe_redirect_sessions',
    method: 'POST',
    data: {
      payment_intent_id: paymentIntentId,
      form_data: formData
    }
  })

  if (!success) {
    dispatch(handleErrorResponse(responseData))
  }

  return success
}

const deleteStripeRedirectSession = (paymentIntentId) => async () => {
  if (!paymentIntentId) return

  await httpRequest({
    url: `/api/booking/stripe_redirect_sessions/${paymentIntentId}`,
    method: 'DELETE'
  })
}

const getEventLabel = (eventType) => {
  if (eventType.payment_method) {
    return i18n.t('booking.analytics.invitee_meeting_scheduled', {
      name: eventType.name,
      currency: eventType.payment_method.currency
    })
  }
  return eventType.name
}

const trackMeetingConfirmedFromClient = (client, eventType, userUuid) => {
  const trackProperties = {
    source: 'sidebar',
    platform: client?.platform,
    et_kind: eventType.kind,
    et_ownership: eventType.owner_type === 'User' ? 'personal' : 'other',
    et_pooling: eventType.pooling_type,
    et_settings_edited: false,
    event_type_id: eventType.uuid,
    required_questions: eventType.custom_fields?.some((field) => field.required) || false,
  }

  Segment.track('book_on_behalf.success', trackProperties)
  eventService.dispatch({
    name: 'Flow Step Completed',
    version: 1,
    dependencies: {
      user_uuid: userUuid
    },
    entities: ['organization', 'user'],
    properties: {
      flow_name: 'schedule_in_real_time',
      flow_version: 'v1',
      flow_step: 'book',
      completion_method: 'step_completed'
    }
  })
}

const trackMeetingConfirmed = (eventType, invitee, calendarAccounts) => {
  const event = invitee.event
  GoogleAnalytics.userTrackEvent('invitee_meeting_scheduled',
    getEventLabel(eventType),
    eventType.payment_method != null ? eventType.payment_method.amount_cents : undefined)

  EmbedAnalytics.track('event_scheduled', {
    event: {
      uri: event.uri
    },
    invitee: {
      uri: invitee.uri
    }
  })

  const location = (eventType.location_configurations != null && eventType.location_configurations.length > 0)
    ? eventType.location_configurations[0].kind : event.location

  Segment.track('Meeting Created', {
    meeting_type: eventType.type,
    event_type: eventType.kind,
    location,
    meeting_duration: eventType.duration,
    calendar_connected: calendarAccounts !== null,
    team_members_included: event.assigned_to
  })

  return FbPixel.trackCustom('invitee_meeting_scheduled', {
    event_type_name: getEventLabel(eventType),
    amount_cents: (eventType.payment_method != null ? eventType.payment_method.amount_cents : undefined)
  })
}

const trackPollVoteRecordedEvent = (state) => {
  const eventType = eventTypeSelector(state)
  const invitee = inviteeSelector(state)
  const currentUser = currentUserSelector(state)
  const countryCode = countryCodeSelector(state)
  const selectedSpots = selectedSpotsSelector(state)
  const calendarSpotsCount = calendarSpotsCountSelector(state)

  const ua = detect.parse(navigator.userAgent)

  eventService.dispatch({
    name: 'Poll Vote Recorded',
    version: 1,
    dependencies: {
      event_type_uuid: eventType.uuid,
      ...(invitee?.uuid && { invitee_uuid: invitee.uuid }),
      ...(currentUser?.uuid && { user_uuid: currentUser.uuid })
    },
    entities: ['event_type', 'invitee', 'organization', 'user'],
    properties: {
      invitee_browser: ua.browser.name,
      invitee_calendly_user_id: undefined,
      invitee_calendly_user_uuid: currentUser?.uuid,
      invitee_country_code: countryCode,
      invitee_device: `${ua.device.name || ua.device.type} ${ua.os.name}`,
      is_calendly_user: !!(currentUser?.uuid),
      is_owner_session: currentUser?.uuid === eventType.owning_user?.uuid,
      time_slots_offerred_count: calendarSpotsCount,
      time_slots_selected_count: selectedSpots.length
    }
  })
}

export const formFieldMounted = () => ({ type: 'FORM_FIELD_MOUNTED' })

export const formUnmounted = () => ({ type: 'FORM_UNMOUNTED' })

export const formFieldUpdated = (name, value) => (dispatch, getState) => {
  let filledFields = filledFieldsSelector(getState())

  if (value.length === 0) {
    filledFields = filledFields.filter((e) => e !== name)
  } else if (!filledFields.includes(name)) {
    filledFields = filledFields.concat(name)
  }

  return dispatch({
    type: 'FORM_FIELD_UPDATED',
    fields_filled: filledFields
  })
}


const showRecaptchaChallenge = (requestId) => ({
  type: 'SHOW_RECAPTCHA_CHALLENGE',
  payload: { requestId }
})
const recaptchaChallengeFailed = () => ({ type: 'RECAPTCHA_CHALLENGE_FAILED' })
export const dismissRecaptchaChallenge = () => ({ type: 'DISMISS_RECAPTCHA_CHALLENGE' })

const isRecaptchaChallengeRequiredError = (payload = {}) => payload.message === 'recaptcha_challenge_required'
const isRecaptchaChallengeFailedError = (payload = {}) => payload.message === 'recaptcha_challenge_failed'

const showVerificationCodeChallenge = (schedulingParams, errorMessage) => ({
  type: 'SHOW_VERIFICATION_CODE_CHALLENGE',
  payload: { schedulingParams, errorMessage }
})

export const resetVerificationCodeChallenge = () => ({
  type: 'RESET_VERIFICATION_CODE_CHALLENGE',
  payload: { show: false }
})

const couponError = (payload) => ({
  type: 'COUPON_ERROR',
  payload
})

export const resetCouponError = () => ({
  type: 'RESET_COUPON_ERROR',
})

const isVerificationCodeChallengeRequired = (payload = {}) => payload?.invitee?.verification_error === 'verification_required'
const isVerificationCodeChallengeFailed = (payload = {}) => (
  !!payload?.invitee?.verification_error && payload.invitee.verification_error !== 'verification_required'
)

const filterDialingCodeOnly = (field) => {
  if (field.format !== 'phone_number') return field
  if (!field.value) return field

  if (isDialCodeOnly(field.value, field.countryCode)) {
    return { ...field, value: '' }
  }

  return field
}

const getSchedulingData = async (state, { spotStartTime, formData, verificationCode }) => {
  const inRecaptchaChallengeFlow = showRecaptchaChallengeSelector(state)
  const recaptchaV2Token = formData.recaptcha_token_version_2 || ''
  const recaptchaToken = inRecaptchaChallengeFlow ? recaptchaV2Token : await formData.recaptchaVerifyCallback()
  const recaptchaKey = inRecaptchaChallengeFlow ? 'recaptcha_token_version_2' : 'recaptcha_token'
  const schedulingLink = schedulingLinkSelector(state)
  const eventType = eventTypeSelector(state)
  const couponData = formData.couponData
  const bookingCode = couponData?.resource?.code || null
  const { locale, durationOverride, uuid } = eventType
  const eventFields = formData.event_fields
    .map(filterDialingCodeOnly)
    .map(
      ({
        id: _id,
        extension: _extension,
        isValid: _isValid,
        countryCode: _countryCode,
        ...rest
      }) => rest
    )

  const purchasedPackage = purchasedPackageSelector(state)

  return {
    analytics: analyticsDataSelector(state),
    embed: embedSelector(state),
    form_submission_uuid: formSubmissionUuidSelector(state),
    event: schedulingEventSelector(state, { spotStartTime, durationOverride, formData }),
    event_fields: eventFields,
    invitee: inviteeDataSelector(state, { formData }),
    payment_token: paymentTokenSelector(state),
    [recaptchaKey]: recaptchaToken,
    tracking: trackingSelector(state),
    scheduling_link_uuid: schedulingLink.uid,
    booking_flow: formData.bookingFlow,
    creator_uuid: formData.creatorUuid,
    locale,
    verification_code: verificationCode,
    event_type_uuid: uuid,
    booking_code: bookingCode,
    purchased_package_uuid: purchasedPackage?.uuid,
  }
}

/**
 * checkInviteeFingerprintFreshness()
 *
 * Using Stytch, invitees are fingerprinted upon reaching the booking app and then refreshed
 * periodically on a timer. If for some reason this timer-based method has failed and/or the
 * Stytch `telemetryId` is expired upon final booking submit, we will try one last time to
 * generate a fresh one. This is fallback to ensure we always have a `telemetryId` to lookup.
 *
 * @see apps/booking/frontend/contexts/TelemetryContext.js
 */
const checkInviteeFingerprintFreshness = async (state) => {
  const telemetryId = state.tracking?.stytch_telemetry_id
  const expiresAt = state.tracking?.stytch_telemetry_expires_at
  const publicToken = Settings?.trust_and_safety?.stytch_public_token
  const submitURL = 'https://dfp.calendly.com'
  const refreshBefore = Settings?.trust_and_safety?.stytch_telemetry_expiry_buffer || 15_000
  const timeUntilRefresh = expiresAt ? (expiresAt - Date.now() - refreshBefore) : -1
  const SYNC_TELEMETRY_FETCH_SWITCH = Settings?.trust_and_safety?.stytch_telemetry_sync_fetch_switch || false

  if (!publicToken) {
    Logger.warn('Public token is missing. Skipping telemetry ID fetch.')
    return undefined
  }

  // do nothing if switched off or when the existing fingerprint is not expiring soon
  if (
    !SYNC_TELEMETRY_FETCH_SWITCH
    || (telemetryId && timeUntilRefresh > 0)
  ) {
    return undefined
  }

  try {
    // Check if GetTelemetryID is available
    if (typeof window.GetTelemetryID !== 'function') {
      Logger.warn('GetTelemetryID function is not available - telemetry script may not have loaded')
      return undefined
    }

    // Call GetTelemetryID with separate parameters (not an object)
    const newTelemetryId = await window.GetTelemetryID(publicToken, submitURL)

    return newTelemetryId
  } catch (error) {
    Logger.warn('Telemetry ID fetch failed:', error)
  }

  return undefined
}

const hasCouponError = (success, responseData) => {
  if (success) { return false }

  if (responseData.type !== 'validation') { return false }
  const payload = responseData.payload
  if (!payload?.details) { return false }
  return !!payload.details.find((detail) => detail.parameter === 'booking_code')
}

// Handles various booking failure scenarios - returns true if handled, false otherwise
const handleBookingFailure = async (dispatch, getState, success, responseData, context) => {
  const { spotStartTime, formData, paymentApi, requestId, client, numRetries, verificationCode, useAsync, scheduleFn } = context

  if (cardActionRequired(success, responseData) && paymentApi && spotStartTime && formData) {
    if (await paymentApi.handleCardAction(responseData.client_secret)) {
      // send same schedule request again
      dispatch(scheduleFn(spotStartTime, formData, paymentApi, requestId, client))
    }
    return true
  }

  if (duplicateBookingDetected(success, responseData) && numRetries <= 60 && paymentApi && spotStartTime && formData) {
    const retryParams = [spotStartTime, formData, paymentApi, requestId, client, numRetries + 1, verificationCode, useAsync]
    setTimeout(() => dispatch(scheduleFn(...retryParams)), 500)
    return true
  }

  if (isRecaptchaChallengeRequiredError(responseData.payload)) {
    dispatch(showRecaptchaChallenge(requestId))

    const state = getState()
    const eventType = eventTypeSelector(state)
    const telemetryId = state.tracking?.stytch_telemetry_id

    eventService.dispatch({
      name: 'ReCaptcha Interaction',
      version: 2,
      dependencies: {
        user_uuid: eventType.owning_user.uuid,
        organization_uuid: eventType.organization.uuid
      },
      entities: ['user', 'organization'],
      properties: {
        location: 'booking_page',
        action: 'failure',
        event_type_id: eventType.id,
        event_type_uuid: eventType.uuid,
        stytch_telemetry_id: telemetryId
      }
    })

    return true
  }

  if (isRecaptchaChallengeFailedError(responseData.payload)) {
    dispatch(recaptchaChallengeFailed())
    return true
  }

  if (isVerificationCodeChallengeRequired(responseData.payload)) {
    const schedulingParams = { spotStartTime, formData, paymentApi, requestId, client, numRetries }
    dispatch(showVerificationCodeChallenge(schedulingParams, null))
    return true
  }

  if (isVerificationCodeChallengeFailed(responseData.payload)) {
    const schedulingParams = { spotStartTime, formData, paymentApi, requestId, client, numRetries }
    const errorMessage = responseData.payload?.invitee?.verification_error
    dispatch(showVerificationCodeChallenge(schedulingParams, errorMessage))
    return true
  }

  if (hasCouponError(success, responseData)) {
    dispatch(couponError(responseData))
    return true
  }

  // Only call default error handler for actual errors, not successful responses
  if (!success) {
    dispatch(handleErrorResponse(responseData))
    return true
  }

  return false
}

let ACTIVE_POLL_ID = null
let ACTIVE_POLL_CANCELLED = false

const shouldContinuePolling = (requestId) => {
  // Check if polling was explicitly cancelled
  if (ACTIVE_POLL_CANCELLED) {
    return false
  }

  // Check if the br_id is still in the URL - if not, user has navigated away
  const query = parseQuery(window.location.search)
  if (query.br_id !== requestId) {
    return false
  }

  return true
}

export const cancelActivePolling = () => () => {
  ACTIVE_POLL_CANCELLED = true
  ACTIVE_POLL_ID = null
}

const pollBookingStatus = async ({
  dispatch,
  getState,
  requestId,
  formData,
  client,
  initialDelay = 500,
  scheduleFn,
  spotStartTime,
  paymentApi,
  numRetries = 0,
  verificationCode = null,
  useAsync = true
}) => {
  const minDelay = 500
  const maxDelay = 2000
  const maxPollingDuration = 35000 // 35 seconds max polling time
  const startTime = Date.now()

  // Reset cancellation flag for this polling session
  ACTIVE_POLL_CANCELLED = false

  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

  const poll = async (delay) => {
    // Check if we should continue polling before making request
    if (!shouldContinuePolling(requestId)) {
      ACTIVE_POLL_ID = null
      return
    }

    const stateForAttempt = getState()
    const eventTypeForAttempt = eventTypeSelector(stateForAttempt)
    const etUuid = eventTypeForAttempt?.uuid
    const statusUrl = etUuid
      ? `/api/booking/invitees/status/${requestId}?event_type_uuid=${encodeURIComponent(etUuid)}`
      : `/api/booking/invitees/status/${requestId}`

    const [success, response] = await httpRequest({ url: statusUrl, method: 'GET' })

    if (success && response && (response.invitee || response.event)) {
      const state = getState()
      const eventType = eventTypeSelector(state)
      try {
        if (response.event?.scheduled_profile?.email) {
          window.localStorage.setItem('publisherEmail', response.event.scheduled_profile.email)
        }
      } catch (err) {
        Logger.warn('Error accessing localStorage in a private window.', err)
      }

      trackMeetingConfirmed(eventType, response, state.current_user?.calendar_accounnts)
      if (client) {
        const currentUserUuid = currentUserSelector(state)?.uuid || null
        trackMeetingConfirmedFromClient(client, eventType, currentUserUuid)
      }
      dispatch(handleSuccessResponse(response))
      maybeStoreData(state, eventType, formData.invitee)
      dispatch(cleanupAsyncBooking())
      const minimalFormData = formData || {}
      dispatch(afterScheduleNavigation(minimalFormData))
      ACTIVE_POLL_ID = null
      return
    }

    if (!success) {
      const shouldContinue = response?.duplicate_in_progress
      if (!shouldContinue) {
        const pollingContext = {
          spotStartTime,
          formData,
          paymentApi,
          requestId,
          client,
          numRetries,
          verificationCode,
          useAsync,
          scheduleFn
        }
        await handleBookingFailure(dispatch, getState, success, response, pollingContext)
        dispatch(cleanupAsyncBooking(true))
        ACTIVE_POLL_ID = null
        return
      }
    }

    const elapsedTime = Date.now() - startTime
    if (elapsedTime >= maxPollingDuration) {
      dispatch(handleErrorResponse({
        type: 'request_aborted',
        message: i18n.t('booking.error.default')
      }))
      dispatch(cleanupAsyncBooking(true))
      ACTIVE_POLL_ID = null
      return
    }

    // Check again before sleeping to exit early if cancelled
    if (!shouldContinuePolling(requestId)) {
      ACTIVE_POLL_ID = null
      return
    }

    // Calculate next delay with proper backoff, ensuring minimum delay
    const nextDelay = Math.max(minDelay, Math.min(maxDelay, delay * 2))
    await sleep(nextDelay)
    await poll(nextDelay)
  }

  // Ensure we wait before the very first request
  if (initialDelay > 0) {
    await sleep(initialDelay)
  }
  await poll(initialDelay)
}

export const schedule = (
  spotStartTime,
  formData,
  paymentApi,
  requestId,
  client = false,
  numRetries = 0,
  verificationCode = null,
  useAsync = false,
) => async (dispatch, getState) => {
  dispatch({ type: 'SCHEDULING_REQUEST' })

  if (useAsync) {
    dispatch(cleanupAsyncBooking(true))
  }

  let state = getState()

  // before finally submitting the booking request to backend, check if the fingerprint needs to be refreshed
  const newTelemetryId = await checkInviteeFingerprintFreshness(state, dispatch)

  if (newTelemetryId) {
    dispatch({
      type: 'TRACK_STYTCH_TELEMETRY_ID',
      payload: {
        telemetryId: newTelemetryId,
        expiresAt: Date.now() + (5 * 60 * 1000) // refreshed telemetryId expires 5 minutes from now
      }
    })

    state = getState()
  }

  const isBookingAfterStripeRedirect = isBookingAfterStripeRedirectSelector(state)
  const savedFormDataForStripeRedirect = savedFormDataForStripeRedirectSelector(state)

  if (isBookingAfterStripeRedirect) {
    formData.event = savedFormDataForStripeRedirect.event
    formData.event_fields = savedFormDataForStripeRedirect.event_fields
    formData.invitee = savedFormDataForStripeRedirect.invitee
    formData.invitee_guests = savedFormDataForStripeRedirect.invitee_guests
  }

  const data = await getSchedulingData(state, { spotStartTime, formData, verificationCode })

  const headers = {
    'Content-Type': 'application/json',
    'Booking-Request-ID': requestId
  }

  const [success, responseData] = await httpRequest({
    url: useAsync ? '/api/booking/invitees/async' : '/api/booking/invitees',
    method: 'POST',
    headers,
    data
  })

  if (!useAsync && scheduleDone(success, responseData)) {
    const stripeClientSecret = stripeClientSecretSelector(state)
    const paymentIntentId = stripeClientSecret?.value?.split('_secret')[0]
    const shouldCleanupStripeRedirect = saveFormDataForStripeRedirectSelector(state)
    if (shouldCleanupStripeRedirect && paymentIntentId) {
      dispatch(deleteStripeRedirectSession(paymentIntentId))
    }

    try {
      window.localStorage.setItem('publisherEmail', responseData.event.scheduled_profile.email)
    } catch (err) {
      // intentionally do nothing here, but we don't want
      // to break the booking app completely
      Logger.warn('Error accessing localStorage in a private window.', err)
    }

    const eventType = eventTypeSelector(state)

    trackMeetingConfirmed(eventType, responseData, state.current_user.calendar_accounnts)
    if (client) {
      const currentUserUuid = currentUserSelector(state)?.uuid || null
      trackMeetingConfirmedFromClient(client, eventType, currentUserUuid)
    }
    dispatch(handleSuccessResponse(responseData))
    maybeStoreData(state, eventType, formData.invitee)
    dispatch(afterScheduleNavigation(formData))
    return
  }

  // Only handle booking errors that are returned from the async booking endpoint
  if (useAsync) {
    const context = { spotStartTime, formData, paymentApi, requestId, client, numRetries, verificationCode, useAsync, scheduleFn: schedule }
    const handled = await handleBookingFailure(dispatch, getState, success, responseData, context)

    // Check if we should continue with polling
    if (!handled && (success || duplicateBookingDetected(success, responseData))) {
      dispatch(setBookingRequestQueryParam(requestId))
      setFormSnapshot(requestId, formData, client)
      if (ACTIVE_POLL_ID !== requestId) {
        ACTIVE_POLL_ID = requestId
        await pollBookingStatus({
          dispatch,
          getState,
          requestId,
          formData,
          client,
          scheduleFn: schedule,
          spotStartTime,
          paymentApi,
          numRetries,
          verificationCode,
          useAsync
        })
      }
    } else {
      // Error was handled but not a success/duplicate case, so cleanup
      dispatch(cleanupAsyncBooking(true))
    }

    return
  }

  // Sync booking failure handling
  const context = { spotStartTime,
    formData,
    paymentApi,
    requestId,
    client,
    numRetries,
    verificationCode,
    useAsync,
    scheduleFn: schedule }
  await handleBookingFailure(dispatch, getState, success, responseData, context)
}


export const selectLocationConfiguration = (locationConfiguration) => ({
  type: 'SELECT_LOCATION_CONFIGURATION',
  payload: { location_configuration: locationConfiguration }
})

export const onLocationChange = (location) => ({
  type: 'LOCATION_CHANGE',
  payload: { location }
})

export const onPhoneNumberChange = (phoneNumber) => ({
  type: 'PHONE_NUMBER_CHANGE',
  payload: {
    phone_number: phoneNumber
  }
})

export const resendVerificationCode = (schedulingParams, asyncBookingEnabled = false) => async (dispatch) => {
  const { spotStartTime, formData, paymentApi, requestId, client } = schedulingParams

  // Reset any previous error messages
  dispatch({
    type: 'SHOW_VERIFICATION_CODE_CHALLENGE',
    payload: { schedulingParams, errorMessage: null }
  })

  // Trigger a new scheduling request which will send a new verification code
  dispatch(schedule(spotStartTime, formData, paymentApi, requestId, client, 0, null, asyncBookingEnabled))
}

export const submitScheduling = (
  spotStartTime,
  formData,
  paymentApi,
  invitee,
  requestId,
  client,
  useAsync = false,
  verificationCode = null
) => async (dispatch, getState) => {
  // Show loading spinner immediately when button is clicked
  dispatch({ type: 'SCHEDULING_REQUEST' })

  const isBookingAfterStripeRedirect = isBookingAfterStripeRedirectSelector(getState())

  if (isBookingAfterStripeRedirect) {
    // If this is a stripe redirect, all the validations have already been done before the redirect
    // And we've  already injected the previous saved state in <StripeRedirectHandler />
    // just submitting and capturing the payment here
    return dispatch(schedule(spotStartTime, formData, paymentApi, requestId, client, 0, verificationCode, useAsync))
  }

  const isValid = await dispatch(validateScheduling(formData))
  if (!isValid) return undefined

  const isPaymentCreated = await dispatch(createPayment(paymentApi))
  if (!isPaymentCreated) return undefined

  if (invitee) {
    return dispatch(reschedule(spotStartTime, formData, invitee.uuid, requestId))
  }
  return dispatch(schedule(spotStartTime, formData, paymentApi, requestId, client, 0, verificationCode, useAsync))
}

export const resumeAsyncBooking = (requestId) => async (dispatch, getState) => {
  if (!requestId) return
  dispatch({ type: 'SCHEDULING_REQUEST' })
  dispatch(setBookingRequestQueryParam(requestId))
  let formData = {}
  let client = false
  try {
    const raw = sessionStorage.getItem(`ab_form_${requestId}`)
    if (raw) {
      const parsed = JSON.parse(raw)
      formData = parsed.formData || {}
      client = parsed.client || false
    }
  } catch (err) {
    Logger.warn('Unable to read async booking snapshot', err)
  }

  ACTIVE_POLL_ID = requestId
  ACTIVE_POLL_CANCELLED = false
  await pollBookingStatus({
    dispatch,
    getState,
    requestId,
    formData,
    client,
    initialDelay: 0,
    scheduleFn: schedule,
    spotStartTime: null,
    paymentApi: null,
    numRetries: 0,
    verificationCode: null,
    useAsync: true
  })
}

const getPollVotesData = async (state, { formData, startTimes }) => ({
  event_type_uuid: eventTypeSelector(state).uuid,
  invitee: inviteeDataSelector(state, { formData }),
  selected_spots: startTimes.map((startTime) => ({ start_time: startTime }))
})

export const submitPollVotes = (startTimes, formData, requestId) => async (dispatch, getState) => {
  const isValid = await dispatch(validateScheduling(formData))
  if (!isValid) return

  dispatch({ type: 'POLL_VOTES_REQUEST' })

  const state = getState()
  const data = await getPollVotesData(state, { formData, startTimes })
  const requestUrl = data.invitee.uuid ? `/api/booking/poll_votes/${data.event_type_uuid}` : '/api/booking/poll_votes'
  const requestMethod = data.invitee.uuid ? 'PATCH' : 'POST'

  const headers = {
    'Content-Type': 'application/json',
    'Booking-Request-ID': requestId
  }

  const [success, responseData] = await httpRequest({
    url: requestUrl,
    method: requestMethod,
    headers,
    data
  })

  if (success) {
    dispatch({ type: 'POLL_VOTES_SUCCESS', payload: responseData })
    dispatch(setSelectedSpots(data.selected_spots))
    try {
      trackPollVoteRecordedEvent(state)
    } catch (err) {
      Logger.warn('Error tracking Poll Vote Recorded', err)
    }
    dispatch(navigateToConfirmationPage(formData))
  } else {
    dispatch(handleErrorResponse(responseData))
  }
}

export const updateCustomField = (customFieldData) => (dispatch, getState) => {
  const state = getState()
  const previousCustomFields = customFieldsSelector(state) || []
  const eventType = eventTypeSelector(state)
  const definitions = eventType?.custom_fields || []
  const { index, props } = customFieldData

  if (index < 0) return

  let newFields
  if (previousCustomFields.length > 0) {
    newFields = previousCustomFields.slice()
  } else if (definitions.length > 0) {
    newFields = definitions.map((field) => ({
      ...field,
      value: '',
    }))
  } else {
    return
  }

  if (index >= newFields.length) return

  const definition = definitions[index]
  const existing = newFields[index]
  const base = definition
    ? { ...definition, ...(existing || {}) }
    : existing

  if (!base) return

  newFields[index] = Object.assign({}, base, props)
  dispatch({
    type: 'CUSTOM_FIELD_UPDATE',
    payload: newFields
  })
}

export const setInitialCustomField = (customFieldData) => ({
  type: 'CUSTOM_FIELD_INITIAL_SET',
  payload: customFieldData
})

export const setSavedFormDataAfterStripeRedirect = (formData) => ({
  type: 'SET_SAVED_FORM_DATA_AFTER_STRIPE_REDIRECT',
  payload: formData
})

export const setIsBookingAfterStripeRedirect = (value) => ({
  type: 'SET_IS_BOOKING_AFTER_STRIPE_REDIRECT',
  payload: value
})
